import { ReactNode, useEffect } from 'react'

import { EmbroiderableProductEmbroideryConfig } from '../../../types/graphql/product'
import { dtcEmbroideryRules } from '../combination-rules/flows/dtcEmbroidery'

import { useExternalEmbroideryFacadeValue } from './adapters/useExternalEmbroideryFacadeValue'
import {
  EmbroideryAvailabilityProvider,
  useEmbroideryAvailability,
} from './EmbroideryAvailabilityProvider'
import { EmbroideryGarmentProvider } from './EmbroideryGarmentProvider'
import { EmbroideryPreferencesProvider } from './EmbroideryPreferencesProvider'
import { EmbroideryPricingProvider } from './EmbroideryPricingProvider'
import {
  EmbroiderySelectionInitialState,
  EmbroiderySelectionProvider,
  useEmbroiderySelection,
} from './EmbroiderySelectionProvider'
import { ExternalEmbroideryFacadeProvider, ExternalEmbroideryFormKind } from './facade'

export type DtcEmbroideryProviderProps = {
  productEmbroideryConfig: EmbroiderableProductEmbroideryConfig | null | undefined
  productColorHexCodes?: readonly string[] | null
  /** The selection to start from instead of an empty one. Read once, on mount. */
  initialSelection?: EmbroiderySelectionInitialState
  /** Called with the whole selection each time it changes. */
  onSelectionChange?: (selection: EmbroiderySelectionInitialState) => void
  /** Called when the page asks for the modal on one kind, or clears the kind. */
  onSelectFormKind: (formKind: ExternalEmbroideryFormKind | null) => void
  /** Called when the page asks for the modal to open. */
  onOpenModal: () => void
  children?: ReactNode
}

/**
 * Everything a DTC product page needs to serve embroidery from the new providers: the providers
 * themselves, in the order they read each other, and the external contract filled from them so
 * the summary card, the CTA and the cart work unchanged.
 *
 * It holds no modal. The caller owns whether the modal is open, and hears about it through
 * `onSelectFormKind` and `onOpenModal`.
 */
export const DtcEmbroideryProvider = ({
  productEmbroideryConfig,
  productColorHexCodes,
  initialSelection,
  onSelectionChange,
  onSelectFormKind,
  onOpenModal,
  children,
}: DtcEmbroideryProviderProps) => (
  <EmbroideryGarmentProvider
    initialState={{ productEmbroideryConfig, productColorHexCodes, rules: dtcEmbroideryRules }}
  >
    <EmbroideryPreferencesProvider initialState={{ enabled: false }}>
      <EmbroiderySelectionProvider initialState={{ initialSelection, onSelectionChange }}>
        <EmbroideryAvailabilityProvider>
          <EmbroideryPricingProvider initialState={{ embroideryPurpose: 'DTC' }}>
            <ExternalEmbroideryFacadeFill
              onSelectFormKind={onSelectFormKind}
              onOpenModal={onOpenModal}
            >
              {children}
            </ExternalEmbroideryFacadeFill>
          </EmbroideryPricingProvider>
        </EmbroideryAvailabilityProvider>
      </EmbroiderySelectionProvider>
    </EmbroideryPreferencesProvider>
  </EmbroideryGarmentProvider>
)

const ExternalEmbroideryFacadeFill = ({
  onSelectFormKind,
  onOpenModal,
  children,
}: Pick<DtcEmbroideryProviderProps, 'onSelectFormKind' | 'onOpenModal' | 'children'>) => {
  usePruneItemsTheGarmentDoesNotOffer()

  const facade = useExternalEmbroideryFacadeValue({
    embroideryPurpose: 'DTC',
    withPricing: true,
    onSelectFormKind,
    onOpenModal,
  })

  return (
    <ExternalEmbroideryFacadeProvider value={facade}>{children}</ExternalEmbroideryFacadeProvider>
  )
}

/**
 * Removes items the garment no longer offers a place for, which happens when a color switch
 * brings a config without that type or placement. Nothing on the page can delete such an item,
 * and it blocks add to bag, so leaving it would strand the customer. Everything else is kept.
 */
const usePruneItemsTheGarmentDoesNotOffer = () => {
  const { items, removeItem } = useEmbroiderySelection()
  const { matrix } = useEmbroideryAvailability()

  useEffect(() => {
    items
      .filter(
        item =>
          !matrix[item.typeKey].placements.some(
            placement => placement.placementId === item.placementId && placement.offered
          )
      )
      .forEach(item => removeItem(item.instanceId))
  }, [items, matrix, removeItem])
}
